/**
 * Self-test suite for the SW encrypted-push pipeline.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/NSEPushSelfTest.swift`.
 *
 * Coverage:
 *
 *   A. Payload parsing (SWPushPayloadParser):
 *      A1. valid dict → decoded fields
 *      A2. missing "kaether" key → rejected
 *      A3. missing envelope field → rejected
 *      A4. missing fromUserId ("from") field → rejected
 *      A5. missing messageId ("msgId") field → rejected
 *      A6. empty envelope → rejected
 *      A7. envelope > MAX_ENVELOPE_CHARS → rejected (oversize)
 *
 *   B. Render policy (SWRenderPolicy.render):
 *      B1. decrypt failure (null decryptResult) → generic
 *      B2. muted thread → generic
 *      B3. successful decrypt + sender in roster → title = roster name
 *      B4. successful decrypt + sender NOT in roster → title = "Message"
 *      B5. server-supplied `from_name` in wire payload is IGNORED
 *          (hostile payload sets from_name = "EVIL"; assert it never appears)
 *      B6. body exactly 250 unicode scalars → not truncated
 *      B7. body 251 unicode scalars → truncated to 250 + "…"
 *      B8. body 1000 unicode scalars → bounded to 250 + "…"
 *      B9. thread display name (group) overrides sender name
 *
 *   C. Duplicate guard (SWDuplicateGuard):
 *      C1. fresh guard → shouldDeliver = true for new pair
 *      C2. same pair within 30s after recordProcessed → shouldDeliver = false
 *      C3. same pair after 31s → shouldDeliver = true
 *      C4. different pairs tracked independently
 *      C5. persistence — write entry, open new guard instance (same IDB),
 *          entry still blocks delivery within window
 *
 *   D. Hardening invariants:
 *      D1. generic outcome carries no associated peer/server input
 *          (two generics from radically different inputs must be structurally
 *          equal — proves the tag has no associated values that could leak)
 *      D2. decryptResult=null ALWAYS yields generic (exhaustive sweep across
 *          all other-input combinations)
 *
 * Total: 20 test cases (≥ the 9 required by the task spec).
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  SWPushPayloadParser,
  SWPushPayloadError,
} from "./SWPushPayload";
import type { SWPushKaetherPayload } from "./SWPushPayload";
import {
  render,
  GENERIC_TITLE,
  UNRESOLVED_SENDER_TITLE,
} from "./SWRenderPolicy";
import type { LocalRoster } from "./SWRenderPolicy";
import { SWDuplicateGuard } from "./SWDuplicateGuard";

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

function samplePayload(): SWPushKaetherPayload {
  return {
    envelope: "kaether:e2ee:v1:fixture",
    fromUserId: "alice",
    messageId: "01HZTEST0000",
  };
}

/**
 * Build a minimal LocalRoster. All values come from this in-test map —
 * never from any wire payload field.
 */
function makeRoster(opts: {
  name?: string;
  inRoster?: boolean;
  threadName?: string;
  muted?: boolean;
}): LocalRoster {
  return {
    senderName: () => opts.name,
    isSenderInRoster: () => opts.inRoster ?? false,
    threadDisplayName: () => opts.threadName,
    isThreadMuted: () => opts.muted ?? false,
  };
}

/** Generate an isolated IDB name for each test so cases don't cross-contaminate. */
function freshDbName(): string {
  return `fr.kaether.sw-dedup.test.${Math.random().toString(36).slice(2)}`;
}

// ---------------------------------------------------------------------------
// A. Payload parsing
// ---------------------------------------------------------------------------

async function testParseValidDict(): Promise<TestCase> {
  const name = "A1: parse valid dict → decoded fields";
  const raw = {
    aps: { alert: { title: "ignored", body: "ignored" } },
    kaether: {
      envelope: "kaether:e2ee:v1:abc",
      from: "alice",
      msgId: "01HZX1234",
    },
  };
  try {
    const p = SWPushPayloadParser.parse(raw);
    const ok =
      p.envelope === "kaether:e2ee:v1:abc" &&
      p.fromUserId === "alice" &&
      p.messageId === "01HZX1234";
    return ok ? pass(name) : fail(name, `got ${JSON.stringify(p)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testParseMissingKaetherKey(): Promise<TestCase> {
  const name = "A2: missing kaether key → rejected";
  try {
    SWPushPayloadParser.parse({ aps: { alert: "x" } });
    return fail(name, "did not throw");
  } catch (e) {
    if (e instanceof SWPushPayloadError && e.kind.tag === "missingKaetherKey") {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testParseMissingEnvelope(): Promise<TestCase> {
  const name = "A3: missing envelope field → rejected";
  try {
    SWPushPayloadParser.parse({ kaether: { from: "alice", msgId: "m1" } });
    return fail(name, "did not throw");
  } catch (e) {
    if (e instanceof SWPushPayloadError && e.kind.tag === "missingEnvelope") {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testParseMissingFromUserId(): Promise<TestCase> {
  const name = "A4: missing fromUserId → rejected";
  try {
    SWPushPayloadParser.parse({ kaether: { envelope: "abc", msgId: "m1" } });
    return fail(name, "did not throw");
  } catch (e) {
    if (e instanceof SWPushPayloadError && e.kind.tag === "missingFromUserId") {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testParseMissingMessageId(): Promise<TestCase> {
  const name = "A5: missing messageId → rejected";
  try {
    SWPushPayloadParser.parse({ kaether: { envelope: "abc", from: "alice" } });
    return fail(name, "did not throw");
  } catch (e) {
    if (e instanceof SWPushPayloadError && e.kind.tag === "missingMessageId") {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testParseEmptyEnvelope(): Promise<TestCase> {
  const name = "A6: empty envelope → rejected";
  try {
    SWPushPayloadParser.parse({
      kaether: { envelope: "", from: "alice", msgId: "m1" },
    });
    return fail(name, "did not throw");
  } catch (e) {
    if (e instanceof SWPushPayloadError && e.kind.tag === "emptyEnvelope") {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testParseOversizeEnvelope(): Promise<TestCase> {
  const name = "A7: oversize envelope → rejected";
  const envelope = "A".repeat(SWPushPayloadParser.MAX_ENVELOPE_CHARS + 1);
  try {
    SWPushPayloadParser.parse({
      kaether: { envelope, from: "alice", msgId: "m1" },
    });
    return fail(name, "did not throw");
  } catch (e) {
    if (
      e instanceof SWPushPayloadError &&
      e.kind.tag === "oversizeEnvelope" &&
      e.kind.length === SWPushPayloadParser.MAX_ENVELOPE_CHARS + 1
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// B. Render policy
// ---------------------------------------------------------------------------

async function testRenderDecryptFailureIsGeneric(): Promise<TestCase> {
  const name = "B1: decrypt failure (null) → generic";
  const outcome = render(
    samplePayload(),
    null,
    makeRoster({ name: "Alice", inRoster: true }),
  );
  const ok = outcome.tag === "generic";
  return ok ? pass(name) : fail(name, `got tag=${outcome.tag}`);
}

async function testRenderMutedThreadIsGeneric(): Promise<TestCase> {
  const name = "B2: muted thread → generic";
  const outcome = render(
    samplePayload(),
    "hello",
    makeRoster({ name: "Alice", inRoster: true, muted: true }),
  );
  const ok = outcome.tag === "generic";
  return ok ? pass(name) : fail(name, `got tag=${outcome.tag}`);
}

async function testRenderSenderInRosterUsesRosterName(): Promise<TestCase> {
  const name = "B3: sender in roster → title = roster name";
  const outcome = render(
    samplePayload(),
    "hi there",
    makeRoster({ name: "Alice", inRoster: true }),
  );
  if (outcome.tag !== "rendered") {
    return fail(name, `got tag=${outcome.tag}`);
  }
  const ok = outcome.display.title === "Alice" && outcome.display.body === "hi there";
  return ok
    ? pass(name)
    : fail(name, `title=${outcome.display.title} body=${outcome.display.body}`);
}

async function testRenderSenderNotInRosterUsesFallback(): Promise<TestCase> {
  const name = "B4: sender NOT in roster → title = \"Message\" (no peer-leak)";
  const outcome = render(
    samplePayload(),
    "hi",
    makeRoster({ name: "Alice", inRoster: false }),
  );
  if (outcome.tag !== "rendered") {
    return fail(name, `got tag=${outcome.tag}`);
  }
  const ok = outcome.display.title === UNRESOLVED_SENDER_TITLE;
  return ok
    ? pass(name)
    : fail(name, `title=${outcome.display.title}`);
}

async function testRenderFromNameFieldIsIgnored(): Promise<TestCase> {
  // CRITICAL SECURITY TEST.
  // The wire payload carries a hostile `from_name` = "EVIL" field. Assert
  // that "EVIL" never appears in the rendered notification. The render
  // function has no `fromName` parameter by design.
  const name = "B5: server-supplied from_name field is IGNORED (no peer name leak)";

  // Build the raw wire payload with a hostile from_name.
  const rawPayload = {
    kaether: {
      envelope: "kaether:e2ee:v1:test",
      from: "mallory",
      msgId: "99ZZ",
      from_name: "EVIL", // hostile server-injected field
    },
  };

  // Parse through the payload parser (which strips everything except
  // envelope/from/msgId from the kaether sub-object — from_name is dropped).
  let payload: SWPushKaetherPayload;
  try {
    payload = SWPushPayloadParser.parse(rawPayload);
  } catch (e) {
    return fail(name, `parse threw: ${String(e)}`);
  }

  // Render with a roster that has NO entry for "mallory".
  const outcome = render(
    payload,
    "secret message",
    makeRoster({ inRoster: false }), // no name in roster
  );

  if (outcome.tag !== "rendered") {
    return fail(name, `got tag=${outcome.tag}`);
  }

  const titleHasEvil = outcome.display.title.includes("EVIL");
  const bodyHasEvil = outcome.display.body.includes("EVIL");

  if (titleHasEvil || bodyHasEvil) {
    return fail(
      name,
      `EVIL appeared in output — title="${outcome.display.title}" body="${outcome.display.body}"`,
    );
  }

  // The title should be the unresolved sender fallback ("Message"), not "EVIL".
  const ok = outcome.display.title === UNRESOLVED_SENDER_TITLE;
  return ok
    ? pass(name)
    : fail(name, `title=${outcome.display.title} (expected "${UNRESOLVED_SENDER_TITLE}")`);
}

async function testRenderBodyAt250ScalarsNotTruncated(): Promise<TestCase> {
  const name = "B6: body exactly 250 unicode scalars → not truncated";
  const body = "x".repeat(250);
  const outcome = render(
    samplePayload(),
    body,
    makeRoster({ name: "Alice", inRoster: true }),
  );
  if (outcome.tag !== "rendered") {
    return fail(name, `got tag=${outcome.tag}`);
  }
  const scalarLen = [...outcome.display.body].length;
  const ok = outcome.display.body === body && scalarLen === 250;
  return ok
    ? pass(name)
    : fail(name, `len=${scalarLen} hasSuffix=${outcome.display.body.endsWith("…")}`);
}

async function testRenderBody251ScalarsTruncated(): Promise<TestCase> {
  const name = "B7: body 251 unicode scalars → truncated to 250 + \"…\"";
  const body = "x".repeat(251);
  const outcome = render(
    samplePayload(),
    body,
    makeRoster({ name: "Alice", inRoster: true }),
  );
  if (outcome.tag !== "rendered") {
    return fail(name, `got tag=${outcome.tag}`);
  }
  const expected = "x".repeat(250) + "…";
  const ok = outcome.display.body === expected;
  return ok
    ? pass(name)
    : fail(
        name,
        `len=${[...outcome.display.body].length} hasSuffix=${outcome.display.body.endsWith("…")}`,
      );
}

async function testRenderBody1000ScalarsBounded(): Promise<TestCase> {
  const name = "B8: body 1000 unicode scalars → bounded to 250 + \"…\"";
  const body = "y".repeat(1000);
  const outcome = render(
    samplePayload(),
    body,
    makeRoster({ name: "Alice", inRoster: true }),
  );
  if (outcome.tag !== "rendered") {
    return fail(name, `got tag=${outcome.tag}`);
  }
  const expected = "y".repeat(250) + "…";
  const ok = outcome.display.body === expected;
  return ok
    ? pass(name)
    : fail(name, `len=${[...outcome.display.body].length}`);
}

async function testRenderThreadNameOverridesSenderName(): Promise<TestCase> {
  const name = "B9: thread display name (group) overrides sender name";
  const outcome = render(
    samplePayload(),
    "hi",
    makeRoster({ name: "Alice", inRoster: true, threadName: "Project Group" }),
  );
  if (outcome.tag !== "rendered") {
    return fail(name, `got tag=${outcome.tag}`);
  }
  const ok = outcome.display.title === "Project Group";
  return ok
    ? pass(name)
    : fail(name, `title=${outcome.display.title}`);
}

// ---------------------------------------------------------------------------
// C. Duplicate guard
// ---------------------------------------------------------------------------

async function testGuardFreshReturnsTrue(): Promise<TestCase> {
  const name = "C1: fresh guard → shouldDeliver = true for new pair";
  const g = new SWDuplicateGuard({ dbName: freshDbName(), dedupWindowSeconds: 30 });
  const deliver = await g.shouldDeliver(
    { fromUserId: "alice", messageId: "m1" },
    1000 * 1000,
  );
  return deliver ? pass(name) : fail(name, "should have allowed first delivery");
}

async function testGuardWithinWindowReturnsFalse(): Promise<TestCase> {
  const name = "C2: same pair within 30s → shouldDeliver = false";
  const g = new SWDuplicateGuard({ dbName: freshDbName(), dedupWindowSeconds: 30 });
  const t0 = 1_000_000;
  await g.recordProcessed({ fromUserId: "alice", messageId: "m1" }, t0);
  const deliver = await g.shouldDeliver(
    { fromUserId: "alice", messageId: "m1" },
    t0 + 5_000, // 5s later — within 30s window
  );
  return !deliver ? pass(name) : fail(name, "should have blocked re-delivery");
}

async function testGuardOutsideWindowReturnsTrue(): Promise<TestCase> {
  const name = "C3: same pair after 31s → shouldDeliver = true";
  const g = new SWDuplicateGuard({ dbName: freshDbName(), dedupWindowSeconds: 30 });
  const t0 = 1_000_000;
  await g.recordProcessed({ fromUserId: "alice", messageId: "m1" }, t0);
  const deliver = await g.shouldDeliver(
    { fromUserId: "alice", messageId: "m1" },
    t0 + 31_000, // 31s later — outside 30s window
  );
  return deliver ? pass(name) : fail(name, "should have allowed re-delivery after window");
}

async function testGuardMultiplePairsTrackedIndependently(): Promise<TestCase> {
  const name = "C4: different pairs tracked independently";
  const g = new SWDuplicateGuard({ dbName: freshDbName(), dedupWindowSeconds: 30 });
  const t0 = 1_000_000;
  await g.recordProcessed({ fromUserId: "alice", messageId: "m1" }, t0);
  await g.recordProcessed({ fromUserId: "bob", messageId: "m2" }, t0);
  const t1 = t0 + 5_000;

  const aliceBlocked = !(await g.shouldDeliver({ fromUserId: "alice", messageId: "m1" }, t1));
  const bobBlocked = !(await g.shouldDeliver({ fromUserId: "bob", messageId: "m2" }, t1));
  const aliceM2Allowed = await g.shouldDeliver({ fromUserId: "alice", messageId: "m2" }, t1);
  const bobM1Allowed = await g.shouldDeliver({ fromUserId: "bob", messageId: "m1" }, t1);

  const ok = aliceBlocked && bobBlocked && aliceM2Allowed && bobM1Allowed;
  return ok
    ? pass(name)
    : fail(
        name,
        `ab=${aliceBlocked} bb=${bobBlocked} am2=${aliceM2Allowed} bm1=${bobM1Allowed}`,
      );
}

async function testGuardPersistenceAcrossRestarts(): Promise<TestCase> {
  const name = "C5: persistence — entry survives SW restart (new guard instance, same IDB)";
  const dbName = freshDbName();
  const t0 = 1_000_000;

  // "First SW run": record that we processed this pair.
  const writer = new SWDuplicateGuard({ dbName, dedupWindowSeconds: 30 });
  await writer.recordProcessed({ fromUserId: "alice", messageId: "m1" }, t0);

  // "SW restart": create a brand-new guard instance pointing at the same IDB.
  const reader = new SWDuplicateGuard({ dbName, dedupWindowSeconds: 30 });
  const deliver = await reader.shouldDeliver(
    { fromUserId: "alice", messageId: "m1" },
    t0 + 5_000, // 5s later — within window
  );

  return !deliver
    ? pass(name)
    : fail(name, "dedup state did not survive SW restart (IDB not persisted)");
}

// ---------------------------------------------------------------------------
// D. Hardening invariants
// ---------------------------------------------------------------------------

async function testGenericCasHasNoAssociatedValues(): Promise<TestCase> {
  // Two completely different inputs → both generic → must be structurally
  // identical. This proves `{ tag: "generic" }` has no associated values
  // that could differentiate (i.e. leak) inputs.
  const name = "D1: generic carries no associated peer/server input (two generics are equal)";

  const g1 = render(
    samplePayload(),
    null, // decrypt failure
    makeRoster({ name: "Alice", inRoster: true, threadName: "Friends" }),
  );
  const g2 = render(
    { envelope: "z", fromUserId: "mallory", messageId: "m-evil" },
    "ultra secret", // decrypted body present but thread is muted
    makeRoster({ name: "Mallory", inRoster: true, muted: true }),
  );

  const ok =
    g1.tag === "generic" &&
    g2.tag === "generic" &&
    JSON.stringify(g1) === JSON.stringify(g2);
  return ok ? pass(name) : fail(name, `g1=${JSON.stringify(g1)} g2=${JSON.stringify(g2)}`);
}

async function testNullDecryptResultAlwaysGenericExhaustive(): Promise<TestCase> {
  // Sweep every combination of other inputs; with decryptResult=null the
  // outcome MUST always be generic.
  const name = "D2: decryptResult=null ALWAYS yields generic (exhaustive sweep)";

  const names: Array<string | undefined> = [undefined, "Alice"];
  const inRosters: boolean[] = [false, true];
  const threadNames: Array<string | undefined> = [undefined, "Group"];
  const muteds: boolean[] = [false, true];

  for (const n of names) {
    for (const r of inRosters) {
      for (const t of threadNames) {
        for (const m of muteds) {
          const outcome = render(
            samplePayload(),
            null,
            makeRoster({ name: n, inRoster: r, threadName: t, muted: m }),
          );
          if (outcome.tag !== "generic") {
            return fail(
              name,
              `got tag=${outcome.tag} for n=${n} r=${r} t=${t} m=${m}`,
            );
          }
        }
      }
    }
  }
  return pass(name);
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "SW encrypted push (payload + render + dedup)",
  isVerified: true,
  run: async () =>
    buildSuiteResult(
      await Promise.all([
        // A. Payload parsing (7 cases)
        testParseValidDict(),
        testParseMissingKaetherKey(),
        testParseMissingEnvelope(),
        testParseMissingFromUserId(),
        testParseMissingMessageId(),
        testParseEmptyEnvelope(),
        testParseOversizeEnvelope(),
        // B. Render policy (9 cases)
        testRenderDecryptFailureIsGeneric(),
        testRenderMutedThreadIsGeneric(),
        testRenderSenderInRosterUsesRosterName(),
        testRenderSenderNotInRosterUsesFallback(),
        testRenderFromNameFieldIsIgnored(),
        testRenderBodyAt250ScalarsNotTruncated(),
        testRenderBody251ScalarsTruncated(),
        testRenderBody1000ScalarsBounded(),
        testRenderThreadNameOverridesSenderName(),
        // C. Duplicate guard (5 cases)
        testGuardFreshReturnsTrue(),
        testGuardWithinWindowReturnsFalse(),
        testGuardOutsideWindowReturnsTrue(),
        testGuardMultiplePairsTrackedIndependently(),
        testGuardPersistenceAcrossRestarts(),
        // D. Hardening invariants (2 cases)
        testGenericCasHasNoAssociatedValues(),
        testNullDecryptResultAlwaysGenericExhaustive(),
      ]),
    ),
});
